'use client';

import { FC, useEffect, useState } from 'react';
import Image from 'next/image';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { twMerge } from 'tailwind-merge';
import { toast } from 'react-toastify';
import { Svg, Text } from '@/components';
import { ShareIcon } from '@/icons';
import { pushGA4Event } from '@/utils/helper';

const OPTIONS = [
  {
    label: 'Copy',
    icon: '/vectors/copy-in-share-icon.svg',
  },
  {
    label: 'Mail',
    icon: '/vectors/mail-in-share-icon.svg',
  },
  {
    label: 'WhatsApp',
    icon: '/vectors/whatsapp-in-share-icon.svg',
  },
];

type ClassNamesTypes = {
  wrapper?: string;
  btn?: string;
  svg?: string;
};

type ShareButtonPropTypes = {
  details?: {
    link: string;
    title: string;
    type: 'product' | 'recipe' | 'story';
  };
  classNames?: ClassNamesTypes;
  multipleDetails?: {
    link: string;
    title: string;
    type: 'product' | 'recipe' | 'story';
  }[];
  shelfData?: string;
};

const ShareIconButton: FC<ShareButtonPropTypes> = ({
  classNames,
  details,
  shelfData,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const onIconClick = (e: any, item: any) => {
    e.stopPropagation();
    e.preventDefault();

    // GA4 Social: share_click
    pushGA4Event('share_click', {
      item_id: shelfData ? 'shelf' : details?.link || '',
    });

    if (!shelfData) {
      if (item === 'Copy') {
        if (details?.link) {
          navigator?.clipboard?.writeText(
            `${window.location.origin}${details?.link}`
          );
        }
        toast.success('Link copied to clipboard successfully!');
      } else if (item === 'Mail') {
        window.location.href = `mailto:?subject=I ♥ this ${
          details?.type
        } on Foodstories!&body=${details?.title}. Here's the link ${
          details?.link && window.location.origin
        }${details?.link}`;
      } else if (item === 'WhatsApp') {
        window.open(
          `https://wa.me/?text=I loved this ${
            details?.type
          } on Foodstories! Here's the link ${
            details?.link && window.location.origin
          }${details?.link}`,
          '_blank'
        );
      }
    } else {
      if (item === 'Copy') {
        navigator?.clipboard?.writeText(shelfData);
        toast.success('Link copied to clipboard successfully!');
      } else if (item === 'Mail') {
        window.location.href = `mailto:?subject=I ♥ these items on my Foodstories Shelf!&body=${encodeURIComponent(
          shelfData
        )}`;
      } else if (item === 'WhatsApp') {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(shelfData)}`,
          '_blank'
        );
      }
    }
    setIsOpen(false); // Close popover after clicking an option
  };

  // Close the popover on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsOpen(false);
    };
    if (isOpen) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        className={twMerge(`rounded-full bg-white p-2`, classNames?.btn)}
        onClick={e => {
          e.stopPropagation();
        }}>
        <Svg
          viewBox='0 0 16 16'
          className={twMerge(`h-4 w-4 stroke-fig`, classNames?.svg)}>
          <ShareIcon />
        </Svg>
      </PopoverTrigger>
      <PopoverContent
        side='top'
        className='w-max rounded-md border border-black/10 bg-white px-2 py-3 lg:max-w-2xl m-3 z-30'>
        <Text className='text-left'>Share Now</Text>
        <div className='flex gap-6 px-3 py-3'>
          {OPTIONS.map((el: { label: string; icon: string }) => (
            <button
              key={el?.label}
              onClick={(e: any) => onIconClick(e, el.label)}
              type='button'
              className='flex flex-col items-center space-y-2'>
              <div className='rounded-full bg-pale-rose p-3'>
                <Image src={el?.icon} alt={el.label} width={16} height={16} />
              </div>
              <span className='text-xs'>{el?.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareIconButton;
